import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  ArrowsOutCardinalIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  PushPinSimpleIcon,
  TrashIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@admin-panel/ui';
import { Toaster, toast } from 'sonner';
import type { OccasionTreeItemType } from '../../schemas/styling';
import {
  useCreateOccasionMutation,
  useDeleteOccasionMutation,
  useGetOccasionsQuery,
  useLazyGetOccasionByIdQuery,
  useMoveOccasionMutation,
  useUpdateOccasionMutation,
} from '../../store/stylist/stylistApi';
import LookOrderDrawer from './components/LookOrderDrawer';
import OccasionFormDrawer from './components/OccasionFormDrawer';
import SortableOccasionItem, { OccasionStatusTag } from './components/SortableOccasionItem';
import {
  filterRootOccasions,
  getOptimizedSquareImage,
  getReorderAnchorIds,
  sortOccasionTree,
  toOccasionImage,
  toUploadFile,
} from './helpers';
import type { DrawerState, LookOrderContext, OccasionFormValues, UploadAssetFile } from './types';

const INITIAL_DRAWER_STATE: DrawerState = {
  open: false,
  mode: 'create',
};

const INITIAL_FORM_VALUES: OccasionFormValues = {
  name: '',
  parent_id: undefined,
  is_active: true,
};

const INITIAL_LOOK_ORDER_CONTEXT: LookOrderContext = {
  open: false,
  occasionId: null,
  occasionName: '',
  subOccasionId: undefined,
  subOccasionName: undefined,
};

const Occasions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRootId, setSelectedRootId] = useState<number | null>(null);

  const [drawerState, setDrawerState] = useState<DrawerState>(INITIAL_DRAWER_STATE);
  const [formValues, setFormValues] = useState<OccasionFormValues>(INITIAL_FORM_VALUES);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isSyncingRootReorder, setIsSyncingRootReorder] = useState(false);
  const [isReorderingSubOccasions, setIsReorderingSubOccasions] = useState(false);
  const [localRoots, setLocalRoots] = useState<Array<OccasionTreeItemType>>([]);

  const [imageFileList, setImageFileList] = useState<Array<UploadAssetFile>>([]);
  const [iconLightFileList, setIconLightFileList] = useState<Array<UploadAssetFile>>([]);
  const [iconDarkFileList, setIconDarkFileList] = useState<Array<UploadAssetFile>>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [lookOrderContext, setLookOrderContext] = useState<LookOrderContext>(
    INITIAL_LOOK_ORDER_CONTEXT,
  );

  const { data, isLoading, isError, error, refetch } = useGetOccasionsQuery();
  const [getOccasionById] = useLazyGetOccasionByIdQuery();
  const [createOccasion, { isLoading: isCreating }] = useCreateOccasionMutation();
  const [updateOccasion, { isLoading: isUpdating }] = useUpdateOccasionMutation();
  const [moveOccasion] = useMoveOccasionMutation();
  const [deleteOccasion, { isLoading: isDeleting }] = useDeleteOccasionMutation();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
  );

  const pushFlash = (tone: 'success' | 'error', message: string) =>
    tone === 'success' ? toast.success(message) : toast.error(message);

  const roots = useMemo(() => sortOccasionTree(data ?? []), [data]);
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const canReorderRoots = normalizedSearchTerm.length === 0;

  useEffect(() => {
    if (!isSyncingRootReorder && !isReorderingSubOccasions) {
      setLocalRoots(roots);
    }
  }, [isReorderingSubOccasions, isSyncingRootReorder, roots]);

  const filteredRoots = useMemo(
    () => filterRootOccasions(localRoots, normalizedSearchTerm),
    [localRoots, normalizedSearchTerm],
  );

  const selectedRoot = useMemo(
    () => localRoots.find(root => root.id === selectedRootId) ?? null,
    [localRoots, selectedRootId],
  );

  const editingOccasionId = drawerState.mode === 'edit' ? drawerState.occasionId : undefined;

  const rootParentOptions = useMemo(() => {
    const onlyRoots = localRoots.filter(root => root.id !== editingOccasionId);
    return onlyRoots.map(root => ({ value: root.id, label: root.name }));
  }, [editingOccasionId, localRoots]);

  useEffect(() => {
    if (!localRoots.length) {
      setSelectedRootId(null);
      return;
    }

    if (!selectedRootId || !localRoots.some(root => root.id === selectedRootId)) {
      setSelectedRootId(localRoots[0].id);
    }
  }, [localRoots, selectedRootId]);

  const closeDrawer = () => {
    setDrawerState(INITIAL_DRAWER_STATE);
    setFormValues(INITIAL_FORM_VALUES);
    setImageFileList([]);
    setIconLightFileList([]);
    setIconDarkFileList([]);
  };

  const openCreateDrawer = (parentId?: number | null) => {
    setDrawerState({
      open: true,
      mode: 'create',
    });
    setFormValues({
      name: '',
      parent_id: parentId ?? undefined,
      is_active: true,
    });
    setImageFileList([]);
    setIconLightFileList([]);
    setIconDarkFileList([]);
  };

  const openEditDrawer = async (occasionId: number) => {
    setEditingId(occasionId);

    try {
      const detail = await getOccasionById(occasionId).unwrap();

      setDrawerState({
        open: true,
        mode: 'edit',
        occasionId: detail.id,
      });
      setFormValues({
        name: detail.name,
        parent_id: detail.parent_id ?? undefined,
        is_active: detail.is_active ?? true,
      });
      setImageFileList(toUploadFile(detail.image));
      setIconLightFileList(toUploadFile(detail.icon_light, 'occasion-icon-light'));
      setIconDarkFileList(toUploadFile(detail.icon_dark, 'occasion-icon-dark'));
    } catch {
      pushFlash('error', 'Could not load occasion details');
    } finally {
      setEditingId(null);
    }
  };

  const handleDelete = async (occasionId: number) => {
    setDeletingId(occasionId);

    try {
      await deleteOccasion(occasionId).unwrap();
      pushFlash('success', 'Occasion deleted');

      if (selectedRootId === occasionId) {
        setSelectedRootId(null);
      }
    } catch {
      pushFlash('error', 'Could not delete occasion');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSave = async (values: OccasionFormValues) => {
    const payload = {
      name: values.name.trim(),
      parent_id: values.parent_id ?? null,
      is_active: values.is_active ?? true,
      image: toOccasionImage(imageFileList),
      icon_light: toOccasionImage(iconLightFileList),
      icon_dark: toOccasionImage(iconDarkFileList),
    };

    try {
      const savedOccasion =
        drawerState.mode === 'edit' && drawerState.occasionId
          ? await updateOccasion({
              id: drawerState.occasionId,
              data: payload,
            }).unwrap()
          : await createOccasion(payload).unwrap();

      pushFlash('success', drawerState.mode === 'edit' ? 'Occasion updated' : 'Occasion created');
      setSelectedRootId(savedOccasion.parent_id ?? savedOccasion.id);
      closeDrawer();
    } catch {
      pushFlash('error', 'Could not save occasion');
    }
  };

  const openLookOrderDrawer = (
    occasionId: number,
    occasionName: string,
    subOccasionId?: number,
    subOccasionName?: string,
  ) => {
    setLookOrderContext({
      open: true,
      occasionId,
      occasionName,
      subOccasionId,
      subOccasionName,
    });
  };

  const handleRootDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!canReorderRoots || isSyncingRootReorder || !over || active.id === over.id) {
      return;
    }

    const activeId = Number(active.id);
    const overId = Number(over.id);
    const oldIndex = localRoots.findIndex(root => root.id === activeId);
    const newIndex = localRoots.findIndex(root => root.id === overId);

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const reorderedRoots = arrayMove(localRoots, oldIndex, newIndex);
    const { beforeId, afterId } = getReorderAnchorIds(reorderedRoots, newIndex);

    setLocalRoots(reorderedRoots);
    setIsSyncingRootReorder(true);

    try {
      await moveOccasion({
        id: activeId,
        data: {
          parent_id: null,
          before_id: beforeId,
          after_id: afterId,
        },
      }).unwrap();

      await refetch();
      pushFlash('success', 'Occasion order updated');
    } catch {
      pushFlash('error', 'Could not update occasion order');
      await refetch();
    } finally {
      setIsSyncingRootReorder(false);
    }
  };

  const handleSubOccasionDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!selectedRoot || isReorderingSubOccasions || !over || active.id === over.id) {
      return;
    }

    const subOccasions = selectedRoot.children ?? [];
    const activeId = Number(active.id);
    const overId = Number(over.id);
    const oldIndex = subOccasions.findIndex(sub => sub.id === activeId);
    const newIndex = subOccasions.findIndex(sub => sub.id === overId);

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const reorderedChildren = arrayMove(subOccasions, oldIndex, newIndex);
    const updatedRoots = localRoots.map(root =>
      root.id === selectedRoot.id ? { ...root, children: reorderedChildren } : root,
    );

    const { beforeId, afterId } = getReorderAnchorIds(reorderedChildren, newIndex);

    setLocalRoots(updatedRoots);
    setIsReorderingSubOccasions(true);

    try {
      await moveOccasion({
        id: activeId,
        data: {
          parent_id: selectedRoot.id,
          before_id: beforeId,
          after_id: afterId,
        },
      }).unwrap();

      await refetch();
      pushFlash('success', 'Sub-occasion order updated');
    } catch {
      pushFlash('error', 'Could not update sub-occasion order');
      await refetch();
    } finally {
      setIsReorderingSubOccasions(false);
    }
  };

  const selectedSubOccasions = selectedRoot?.children ?? [];

  if (isError) {
    return (
      <div className="p-6">
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <WarningCircleIcon size={18} />
              Error loading occasions
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-destructive/90">{String(error)}</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-b from-muted/20 to-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-5"
      >
        <div className="flex items-center justify-between">
          <h1 className="m-0 text-2xl font-semibold text-foreground">Occasion Manager</h1>
        </div>

        <div className="grid min-h-0 grid-cols-1 gap-4 xl:grid-cols-[360px_1fr]">
          <Card className="min-h-0 rounded-xl border border-border bg-card/95">
            <CardHeader className="border-b border-border">
              <div className="flex w-full items-center justify-between gap-2">
                <CardTitle className="text-sm font-semibold">Occasion Taxonomy</CardTitle>
                <Button
                  size="small"
                  icon={<PlusIcon size={14} />}
                  onClick={() => openCreateDrawer(null)}
                >
                  Add Occasion
                </Button>
              </div>
            </CardHeader>
            <CardContent className="min-h-0 overflow-y-auto pt-4">
              <div className="space-y-3">
                <div className="relative">
                  <MagnifyingGlassIcon
                    size={14}
                    className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    className="pl-8"
                    placeholder="Search occasion or sub-occasion"
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                  />
                </div>

                <p className="text-xs text-muted-foreground">
                  {canReorderRoots
                    ? 'Drag top-level occasions to reorder them.'
                    : 'Clear search to reorder top-level occasions.'}
                </p>

                {isLoading ? (
                  <div className="space-y-2">
                    <div className="h-12 animate-pulse rounded-lg border border-border bg-muted/20" />
                    <div className="h-12 animate-pulse rounded-lg border border-border bg-muted/20" />
                    <div className="h-12 animate-pulse rounded-lg border border-border bg-muted/20" />
                  </div>
                ) : filteredRoots.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                    No occasions found
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleRootDragEnd}
                  >
                    <SortableContext
                      items={filteredRoots.map(root => root.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {filteredRoots.map(root => (
                          <SortableOccasionItem
                            key={root.id}
                            occasion={root}
                            active={root.id === selectedRootId}
                            disabled={!canReorderRoots || isSyncingRootReorder}
                            onSelect={() => setSelectedRootId(root.id)}
                            actionSlot={
                              <div
                                className="flex items-center gap-1"
                                onClick={event => event.stopPropagation()}
                              >
                                <Button
                                  size="small"
                                  variant="ghost"
                                  icon={<PencilSimpleIcon size={14} />}
                                  isLoading={editingId === root.id}
                                  onClick={() => openEditDrawer(root.id)}
                                />
                                <Button
                                  size="small"
                                  variant="ghost"
                                  tone="destructive"
                                  icon={<TrashIcon size={14} />}
                                  isLoading={isDeleting && deletingId === root.id}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        'Delete occasion?\nThis will also delete all nested sub-occasions.',
                                      )
                                    ) {
                                      void handleDelete(root.id);
                                    }
                                  }}
                                />
                              </div>
                            }
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="min-h-0 rounded-xl border border-border bg-card/95">
            {!selectedRoot ? (
              <CardContent className="flex min-h-[260px] items-center justify-center text-sm text-muted-foreground">
                Select an occasion to manage details
              </CardContent>
            ) : (
              <>
                <CardHeader className="border-b border-border">
                  <div className="flex items-center gap-3">
                    {selectedRoot.image?.url ? (
                      <img
                        alt={selectedRoot.name}
                        src={getOptimizedSquareImage(selectedRoot.image.url, 240)}
                        className="h-10 w-10 rounded-lg border border-border object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-muted/30 text-muted-foreground">
                        <PushPinSimpleIcon size={16} />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-sm font-semibold">{selectedRoot.name}</CardTitle>
                      <OccasionStatusTag isActive={selectedRoot.is_active} />
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="min-h-0 overflow-y-auto pt-4">
                  {selectedRoot.is_active === false ? (
                    <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                      This occasion is inactive. It remains visible here for curation.
                    </div>
                  ) : null}

                  {selectedSubOccasions.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
                      No sub-occasions yet
                    </div>
                  ) : (
                    <>
                      <p className="mb-3 text-xs text-muted-foreground">
                        Drag sub-occasions to reorder them.
                      </p>

                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleSubOccasionDragEnd}
                      >
                        <SortableContext
                          items={selectedSubOccasions.map(sub => sub.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {selectedSubOccasions.map(sub => (
                              <SortableOccasionItem
                                key={sub.id}
                                occasion={sub}
                                disabled={isReorderingSubOccasions}
                                actionSlot={
                                  <div
                                    className="flex items-center gap-1"
                                    onClick={event => event.stopPropagation()}
                                  >
                                    <Button
                                      size="small"
                                      variant="outline"
                                      icon={<ArrowsOutCardinalIcon size={14} />}
                                      onClick={() =>
                                        openLookOrderDrawer(
                                          selectedRoot.id,
                                          selectedRoot.name,
                                          sub.id,
                                          sub.name,
                                        )
                                      }
                                    >
                                      Curate Looks
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="ghost"
                                      icon={<PencilSimpleIcon size={14} />}
                                      isLoading={editingId === sub.id}
                                      onClick={() => openEditDrawer(sub.id)}
                                    />
                                    <Button
                                      size="small"
                                      variant="ghost"
                                      tone="destructive"
                                      icon={<TrashIcon size={14} />}
                                      isLoading={isDeleting && deletingId === sub.id}
                                      onClick={() => {
                                        if (window.confirm('Delete sub-occasion?')) {
                                          void handleDelete(sub.id);
                                        }
                                      }}
                                    />
                                  </div>
                                }
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </>
                  )}
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </motion.div>

      <OccasionFormDrawer
        open={drawerState.open}
        mode={drawerState.mode}
        parentOptions={rootParentOptions}
        initialValues={formValues}
        imageFileList={imageFileList}
        iconLightFileList={iconLightFileList}
        iconDarkFileList={iconDarkFileList}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isUploadingImage={isUploadingImage}
        onClose={closeDrawer}
        onSubmit={handleSave}
        onImageFileListChange={setImageFileList}
        onIconLightFileListChange={setIconLightFileList}
        onIconDarkFileListChange={setIconDarkFileList}
        onUploadStart={() => setIsUploadingImage(true)}
        onUploadEnd={() => setIsUploadingImage(false)}
      />

      <LookOrderDrawer
        open={lookOrderContext.open}
        occasionId={lookOrderContext.occasionId}
        occasionName={lookOrderContext.occasionName}
        subOccasionId={lookOrderContext.subOccasionId}
        subOccasionName={lookOrderContext.subOccasionName}
        onClose={() =>
          setLookOrderContext(previous => ({
            ...previous,
            open: false,
          }))
        }
      />
      <Toaster richColors position="top-right" />
    </div>
  );
};

export default Occasions;
